import React, { useState } from "react";
import {
  Box,
  Container,
  TextField,
  Typography,
  Button,
  Grid,
  Paper,
  Stack,
} from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import HeroBanner from "../components/commonComponents/HeroBanner";
import { AlignJustify } from "lucide-react";
import { saveContactInquiry } from "../services/contactService";
import { Snackbar, Alert } from "@mui/material";
import ContactInfoSection from "../components/contactUs/ContactInfoSection";
import LocationMap from "../components/contactUs/LocationMap";
import SEO from "../components/commonComponents/SEO";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [popup, setPopup] = useState({
    open: false,
    type: "success",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submittedData, setSubmittedData] = useState(null);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.company.trim())
      newErrors.company = "Company name is required";

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[0-9+\-\s()]{7,20}$/.test(formData.phone)) {
      newErrors.phone = "Enter a valid phone number";
    }

    if (!formData.message.trim()) newErrors.message = "Message is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // const handleSubmit = (e) => {
  //   e.preventDefault();

  //   if (!validate()) return;

  //   setSubmittedData(formData);

  //   console.log("Saved Contact Form Data:", formData);

  //   // API integration can be added here later
  //   // await axios.post("/api/contact", formData);

  //   setFormData({
  //     name: "",
  //     company: "",
  //     email: "",
  //     phone: "",
  //     message: "",
  //   });
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      const docId = await saveContactInquiry(formData);

      console.log("Saved successfully:", docId);

      setSubmittedData(formData);

      setFormData({
        name: "",
        company: "",
        email: "",
        phone: "",
        message: "",
      });

      setPopup({
        open: true,
        type: "success",
        message: "Inquiry submitted successfully!",
      });
    } catch (error) {
      console.error("Error saving contact inquiry:", error);

      setPopup({
        open: true,
        type: "error",
        message: "Submission failed. Please try again.",
      });
    }
  };

  return (
    <>
      <SEO
        title="Contact ZENCON | SAP & Enterprise Technology Experts"
        description="Contact ZENCON Ltd. Co. for SAP consulting, SAP implementation, SAP support, cloud services, digital transformation, automation, and enterprise technology solutions in Saudi Arabia."
        keywords="Contact ZENCON, SAP consultants Saudi Arabia, SAP support KSA, SAP implementation company, digital transformation Saudi Arabia, enterprise technology solutions"
        url="https://www.zencon-sa.com/contact-us"
        image="https://www.zencon-sa.com/images/default-image.png"
      />
      <Snackbar
        open={popup.open}
        autoHideDuration={4000}
        onClose={() =>
          setPopup((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={popup.type}
          variant="filled"
          onClose={() =>
            setPopup((prev) => ({
              ...prev,
              open: false,
            }))
          }
          sx={{
            borderRadius: "14px",
            fontWeight: 600,
            minWidth: "320px",
          }}
        >
          {popup.message}
        </Alert>
      </Snackbar>{" "}
      <HeroBanner title="Contact" subtitle="" />
      {/* Let’s transform your business with smarter SAP solutions. Connect with our experts today */}
      <Box sx={styles.wrapper}>
        <Container maxWidth="lg">
          <Box sx={styles.header}>
            {/* <Typography sx={styles.label}>Contact Us</Typography> */}

            <Typography variant="h4" sx={styles.title}>
              Let’s Build the Future Togethers
            </Typography>

            <Typography sx={styles.subtitle}>
              Whether you are planning a digital transformation journey, SAP
              implementation, cloud migration or business automation initiative
              - Our experts are ready to help.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ sm: 12, md: 6 }}>
              <ContactInfoSection />
            </Grid>
            <Grid size={{ sm: 12, md: 6 }}>
              {" "}
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      error={Boolean(errors.name)}
                      helperText={errors.name}
                      sx={styles.input}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      error={Boolean(errors.company)}
                      helperText={errors.company}
                      sx={styles.input}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={Boolean(errors.email)}
                      helperText={errors.email}
                      sx={styles.input}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      error={Boolean(errors.phone)}
                      helperText={errors.phone}
                      sx={styles.input}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      error={Boolean(errors.message)}
                      helperText={errors.message}
                      sx={styles.input}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, md: 6 }}>
                    <Stack alignItems="flex-end">
                      <Button
                        type="submit"
                        variant="contained"
                        endIcon={<SendRoundedIcon />}
                        sx={styles.button}
                      >
                        Submit Message
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
          <LocationMap />
        </Container>
      </Box>
    </>
  );
};

export default ContactForm;

const styles = {
  wrapper: {
    py: { xs: 8, md: 12 },
    background:
      "radial-gradient(circle at top left, rgba(0,200,83,0.10), transparent 35%), linear-gradient(135deg, #f8fafc 0%, #eef4ff 55%, #e8f5ee 100%)",
    color: "#111827",
  },

  card: {
    p: { xs: 3, md: 5 },
    borderRadius: "28px",
    background: "rgba(255,255,255,0.88)",
    backdropFilter: "blur(18px)",
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow: "0 20px 60px rgba(15,23,42,0.10)",
  },

  header: {
    // textAlign: "center",
    mb: 5,
  },

  label: {
    display: "inline-block",
    px: 2,
    py: 0.8,
    mb: 2,
    borderRadius: "50px",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#00c853",
    background: "rgba(0,200,83,0.10)",
    border: "1px solid rgba(0,200,83,0.22)",
  },

  title: {
    fontWeight: 700,
    fontSize: { xs: "1.8rem", md: "2.5rem" },
    lineHeight: 1.15,
    mb: 2,
    color: "#0f172a",
    textAlign: "center",
  },

  subtitle: {
    maxWidth: 780,
    mx: "auto",
    color: "#475569",
    fontSize: { xs: "0.8rem", md: "1rem" },
    lineHeight: 1.8,
    textAlign: "center",
  },

  input: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      color: "#111827",
      background: "#ffffff",
      transition: "0.3s ease",

      "& fieldset": {
        borderColor: "rgba(15,23,42,0.12)",
      },

      "&:hover fieldset": {
        borderColor: "rgba(0,200,83,0.55)",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#00c853",
        borderWidth: "1.5px",
      },
    },

    "& .MuiInputLabel-root": {
      color: "#64748b",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#00c853",
    },

    "& .MuiFormHelperText-root": {
      ml: 0,
      color: "#dc2626",
    },
  },

  button: {
    mt: 1,
    px: 4,
    py: 1.4,
    borderRadius: "15px",
    fontWeight: 700,
    textTransform: "none",
    color: "#fff",
    background: "linear-gradient(90deg, #00c853, #3e17ff)",
    boxShadow: "0 14px 35px rgba(0,200,83,0.20)",
    transition: "0.3s ease",

    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 18px 45px rgba(62,23,255,0.20)",
      background: "linear-gradient(90deg, #00e676, #512bff)",
    },
  },
};
